import { documentationSections } from '../data/documentationContent'
import type {
  CodeExample,
  DocumentationContent,
  DocumentationItem,
  DocumentationSection,
  DocumentationStep,
  DocumentationTab,
  SearchResult,
} from '../types/documentation.types'

class DocumentationService {
  private sections: DocumentationSection[]

  constructor() {
    this.sections = documentationSections
  }

  getAllSections(): DocumentationSection[] {
    return this.sections
  }

  getSection(sectionId: string): DocumentationSection | undefined {
    return this.sections.find(section => section.id === sectionId)
  }

  getItem(sectionId: string, itemId: string): DocumentationItem | undefined {
    const section = this.getSection(sectionId)
    return section?.items.find(item => item.id === itemId)
  }

  search(query: string): SearchResult[] {
    if (!query.trim()) {
      return []
    }

    const results: SearchResult[] = []
    const searchTerm = query.toLowerCase()

    this.sections.forEach(section => {
      section.items.forEach(item => {
        // Search in title
        if (item.title.toLowerCase().includes(searchTerm)) {
          results.push({
            sectionId: section.id,
            itemId: item.id,
            title: `${section.title} > ${item.title}`,
            snippet: this.getSnippet(
              item.content.description || '',
              searchTerm
            ),
            matchType: 'title',
          })
        }

        // Search in keywords
        if (
          item.searchKeywords?.some(keyword =>
            keyword.toLowerCase().includes(searchTerm)
          )
        ) {
          results.push({
            sectionId: section.id,
            itemId: item.id,
            title: `${section.title} > ${item.title}`,
            snippet: this.getSnippet(
              item.content.description || '',
              searchTerm
            ),
            matchType: 'keywords',
          })
        }

        // Search in content
        const contentText = this.extractTextFromContent(item.content)
        if (contentText.toLowerCase().includes(searchTerm)) {
          results.push({
            sectionId: section.id,
            itemId: item.id,
            title: `${section.title} > ${item.title}`,
            snippet: this.getSnippet(contentText, searchTerm),
            matchType: 'content',
          })
        }
      })
    })

    // Remove duplicates and limit results
    const uniqueResults = results.filter(
      (result, index, self) =>
        index ===
        self.findIndex(
          r => r.sectionId === result.sectionId && r.itemId === result.itemId
        )
    )

    return uniqueResults.slice(0, 10)
  }

  private extractTextFromContent(content: DocumentationContent): string {
    let text = ''

    if (content.description) {
      text += content.description + ' '
    }

    if (content.content) {
      text += content.content + ' '
    }

    if (content.tabs) {
      content.tabs.forEach((tab: DocumentationTab) => {
        text += tab.content + ' '
        if (tab.steps) {
          tab.steps.forEach((step: DocumentationStep) => {
            text += step.title + ' ' + (step.description || '') + ' '
          })
        }
      })
    }

    if (content.codeExamples) {
      content.codeExamples.forEach((example: CodeExample) => {
        if (example.description) {
          text += example.description + ' '
        }
      })
    }

    return text
  }

  private getSnippet(
    text: string,
    searchTerm: string,
    maxLength: number = 120
  ): string {
    const lowerText = text.toLowerCase()
    const lowerTerm = searchTerm.toLowerCase()
    const index = lowerText.indexOf(lowerTerm)

    if (index === -1) {
      return (
        text.substring(0, maxLength) + (text.length > maxLength ? '...' : '')
      )
    }

    const start = Math.max(0, index - 30)
    const end = Math.min(text.length, index + searchTerm.length + 30)

    let snippet = text.substring(start, end)

    if (start > 0) snippet = '...' + snippet
    if (end < text.length) snippet = snippet + '...'

    return snippet
  }
}

export const documentationService = new DocumentationService()
